import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Searchable customer-name/email field — autocompletes from existing invoices.
 * Used inside Finance ledger / receivables / payables.
 */
export default function CustomerSearchField({ value, onChange, label = "Customer", placeholder = "Search by name / email", testid = "customer-search" }) {
  const [q, setQ] = useState(value?.name || value?.email || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  // Sync local input with external value changes (e.g. parent form reset, pre-fill from picker)
  useEffect(() => {
    const next = value?.name || value?.email || "";
    setQ((prev) => (prev === next ? prev : next));
  }, [value?.name, value?.email]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/org/customers/search-v2?q=${encodeURIComponent(q)}`);
        setOpts(data || []);
      } catch { setOpts([]); }
    }, 200);
  }, [q, open]);

  return (
    <div className="space-y-2 relative">
      {label ? <Label>{label}</Label> : null}
      <Input
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        data-testid={testid}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && (opts.length > 0 || q.trim()) ? (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg overflow-hidden" data-testid={`${testid}-results`}>
          {opts.map((o) => (
            <button
              key={o.email + (o.name || "")}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex justify-between"
              onClick={() => {
                onChange?.(o);
                setQ(o.name || o.email);
                setOpen(false);
              }}
              data-testid={`${testid}-option-${(o.email || "").replace(/[^a-z0-9]/gi, "-")}`}
            >
              <div>
                <div className="font-medium">{o.name || "—"}</div>
                <div className="font-mono text-[11px] text-muted-foreground">{o.email}</div>
              </div>
              <div className="font-mono text-xs">₹{Number(o.outstanding || 0).toLocaleString()}</div>
            </button>
          ))}
          {/* Add-new option — always visible when user typed something */}
          {q.trim() && !opts.some((o) => (o.name || "").toLowerCase() === q.toLowerCase().trim()) ? (
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2 border-t border-border bg-muted/40 font-semibold text-orange-600"
              onClick={() => {
                onChange?.({ name: q.trim(), email: "", is_new: true });
                setOpen(false);
              }}
              data-testid={`${testid}-add-new`}
            >
              + Add “{q.trim()}” as a new customer
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
